import { Box, makeStyles } from '@material-ui/core';
import {
  QuoteProductDTO,
  QuoteItemDTO
} from '@setvi/shared/services/react-query';
import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SEmptyState } from '@setvi/shared/components';
import { useQuoteData } from '../../context';
import { ProductsTableHeader } from './components/table-header';
import { ProductRow } from './components/product-row';
import { SectionRow } from './section-row';

const OUTER_GAP = 8;

const useStyles = makeStyles(() => ({
  container: {
    overflow: 'auto',
    position: 'relative',
    flex: 1,
    paddingBottom: 100
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: '#F8F9FA'
  }
}));

interface ProductsTableProps {
  onProductClick?: (product: QuoteProductDTO) => void;
}

/**
 * Products Table - Display and manage quote items
 * Simplified structure following v1 pattern with separate row components
 */
export const ProductsTable = ({ onProductClick }: ProductsTableProps) => {
  const classes = useStyles();
  const { quote } = useQuoteData();
  const [searchParams] = useSearchParams();

  // Get search query from URL
  const searchQuery = searchParams.get('search') || '';

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return quote?.items || [];

    const query = searchQuery.toLowerCase();
    const filtered: QuoteItemDTO[] = [];

    // Helper function to check if an item matches the search query
    const itemMatches = (item: QuoteProductDTO) => {
      const name = item.name?.toLowerCase() || '';
      const sku = item.sku?.toLowerCase() || '';
      const vendor = item.vendor?.name?.toLowerCase() || '';
      return (
        name.includes(query) || sku.includes(query) || vendor.includes(query)
      );
    };

    (quote?.items || []).forEach(item => {
      if (item.type === 'product') {
        const product = item as QuoteProductDTO;

        // Check if product itself matches
        const productMatches = itemMatches(product);

        // Filter matching children
        const matchingChildren = product.children?.filter(child =>
          itemMatches(child as QuoteProductDTO)
        );

        // Include product if it matches OR if any children match
        if (
          productMatches ||
          (matchingChildren && matchingChildren.length > 0)
        ) {
          // If parent matches, show all children; if only children match, show only matching children
          filtered.push({
            ...item,
            children: productMatches ? product.children : matchingChildren
          });
        }
      } else if (item.type === 'section' && 'children' in item) {
        // Filter section products and their children
        const matchingProducts = item.children
          ?.map(child => {
            if (child.type !== 'product') return null;

            const product = child as QuoteProductDTO;
            const productMatches = itemMatches(product);

            // Filter matching children (options) of this product
            const matchingChildren = product.children?.filter(option =>
              itemMatches(option as QuoteProductDTO)
            );

            // Include product if it matches OR if any children match
            if (
              productMatches ||
              (matchingChildren && matchingChildren.length > 0)
            ) {
              // If parent matches, show all children; if only children match, show only matching children
              return {
                ...child,
                children: productMatches ? product.children : matchingChildren
              };
            }

            return null;
          })
          .filter(Boolean);

        // Include section only if it has matching products
        if (matchingProducts && matchingProducts.length > 0) {
          filtered.push({
            ...item,
            children: matchingProducts
          });
        }
      }
    });

    return filtered;
  }, [quote?.items, searchQuery]);

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const item = filteredItems[index];
      // Add gap after each item except the last
      const gap = index < filteredItems.length - 1 ? OUTER_GAP : 0;

      if (item.type === 'section' && 'children' in item) {
        // Section: header + padding + all children (with their options) + gaps
        const children = (item.children || []) as QuoteProductDTO[];
        const childrenGaps =
          children.length > 1 ? (children.length - 1) * 12 : 0;
        let childrenHeight = 0;
        for (let i = 0; i < children.length; i += 1) {
          const child = children[i];
          const optionsCount = child.children?.length || 0;
          childrenHeight += 130 + optionsCount * 134;
        }
        // 60 header + 24 padding (top+bottom) + children + gaps
        return 60 + 24 + childrenHeight + childrenGaps + gap;
      }
      // Standalone product
      const product = item as QuoteProductDTO;
      const optionsCount = product.children?.length || 0;
      return 130 + optionsCount * 134 + gap;
    },
    overscan: 2
  });

  return (
    <div className={classes.container} ref={parentRef}>
      {filteredItems.length > 0 ? (
        <>
          <Box mb={2} className={classes.stickyHeader}>
            <ProductsTableHeader />
          </Box>

          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative'
            }}>
            {virtualizer.getVirtualItems().map(virtualRow => {
              const item = filteredItems[virtualRow.index];

              return (
                <div
                  key={item.id || item._id}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`
                  }}>
                  {item.type === 'section' ? (
                    <SectionRow
                      section={item as any}
                      virtualRow={virtualRow}
                      scrollContainerRef={parentRef}
                      searchQuery={searchQuery}
                      onProductClick={onProductClick}
                    />
                  ) : (
                    <Box mb={2}>
                      <ProductRow
                        product={item as QuoteProductDTO}
                        searchQuery={searchQuery}
                        onProductClick={onProductClick}
                        scrollContainerRef={parentRef}
                        productStartOffset={virtualRow.start}
                      />
                    </Box>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <Box
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <SEmptyState
            title="No Items Found"
            imageSrc="/images/no-my-snippets.png"
            subTitle="Please try again with different search query."
          />
        </Box>
      )}
    </div>
  );
};
